import { type ReactNode, useEffect, useMemo } from 'react';

import { navigationActivityActions } from '@/shared/stores/navigationActivityStore';
import { activeTransferCount } from '../lib/transferActivity';
import {
  expireUploadSession,
  interruptStalledUpload,
  resetUploadRuntime,
} from '../services/uploadEngine';
import { useInterruptedLiveUploads } from '../services/uploadQueries';
import { uploadRuntimeClient } from '../services/uploadRuntimeClient';
import { useUploadHealthStore } from '../stores/uploadHealthStore';
import { useUploadLeadershipStore } from '../stores/uploadLeadershipStore';
import {
  activateUploadsForUser,
  deactivateUploads,
  getActiveUploadUid,
  useUploadStore,
} from '../stores/uploadStore';

/** Owns one UID's upload runtime and health checks above all routed pages. */
export function AuthenticatedUploadScope({
  uid,
  children,
}: {
  uid: string | null;
  children: ReactNode;
}) {
  const items = useUploadStore((state) => state.items);
  const activeCount = activeTransferCount(items);
  const isLeader = useUploadLeadershipStore((state) => state.status === 'leader');
  const sharedWorkerSupported = uploadRuntimeClient.isSupported();
  const sharedHealth = useUploadHealthStore((state) => state.bySessionId);
  const fallbackHealth = useInterruptedLiveUploads(sharedWorkerSupported ? [] : items);
  const expiredSessionIds = useMemo(
    () =>
      sharedWorkerSupported
        ? new Set(
            Object.entries(sharedHealth)
              .filter(([, health]) => health.status === 'expired')
              .map(([sessionId]) => sessionId),
          )
        : fallbackHealth.expiredSessionIds,
    [fallbackHealth.expiredSessionIds, sharedHealth, sharedWorkerSupported],
  );
  const interruptedSessionIds = useMemo(
    () =>
      sharedWorkerSupported
        ? new Set(
            Object.entries(sharedHealth)
              .filter(([, health]) => health.status === 'interrupted')
              .map(([sessionId]) => sessionId),
          )
        : fallbackHealth.interruptedSessionIds,
    [fallbackHealth.interruptedSessionIds, sharedHealth, sharedWorkerSupported],
  );

  useEffect(() => {
    if (!uid) return;
    void activateUploadsForUser(uid).catch((error) => {
      console.error('[uploads] could not activate upload session', error);
    });
    return () => {
      if (getActiveUploadUid() !== uid) return;
      resetUploadRuntime();
      deactivateUploads();
    };
  }, [uid]);

  useEffect(() => {
    if (!isLeader) return;
    for (const item of items) {
      if (!item.backendId) continue;
      if (expiredSessionIds.has(item.backendId)) expireUploadSession(item.id);
      else if (interruptedSessionIds.has(item.backendId)) interruptStalledUpload(item.id);
    }
  }, [expiredSessionIds, interruptedSessionIds, isLeader, items]);

  useEffect(() => {
    navigationActivityActions.setCount('transfers', activeCount);
  }, [activeCount]);

  useEffect(
    () => () => {
      navigationActivityActions.clear('transfers');
    },
    [],
  );

  return children;
}
